import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment";

/** Mirrors backend/src/modules/admin-team/catalogue.ts. */
export interface AdminModule {
  key: string;
  label: string;
  group: string;
  detail: string;
}

/** Where each module's own landing screen lives, in nav order. */
export const MODULE_HOME: { key: string; route: string }[] = [
  { key: "dashboard",       route: "/admin/dashboard" },
  { key: "schools",         route: "/admin/admin-school" },
  { key: "students",        route: "/admin/student" },
  { key: "games",           route: "/admin/game" },
  { key: "coaches",         route: "/admin/coach-data" },
  { key: "events",          route: "/admin/event" },
  { key: "team_entries",    route: "/admin/ManageTeamReport" },
  { key: "certificates",    route: "/admin/manageeventreport" },
  { key: "attendance",      route: "/admin/attendance" },
  { key: "merit",           route: "/admin/merit" },
  { key: "reports",         route: "/admin/report" },
  { key: "payments",        route: "/admin/payment-invoice" },
  { key: "fees",            route: "/admin/sgfi-settings" },
  { key: "affiliation",     route: "/admin/affiliation" },
  { key: "sgfi",            route: "/admin/sgfi-add-student" },
  { key: "student_profile", route: "/admin/profile-view-student" },
  { key: "transfers",       route: "/admin/transfers" },
  { key: "content",         route: "/admin/content" },
  { key: "playzone",        route: "/admin/playzone" },
  { key: "media",           route: "/admin/media-vault" },
  { key: "subscribers",     route: "/admin/subscribers" },
  { key: "mail_settings",   route: "/admin/mail-settings" },
  { key: "user_admin",      route: "/admin/user-mgt" },
];

const MODULES_KEY = "issoAdminModules";
const SUPER_KEY = "issoAdminSuper";

/**
 * What the signed-in administrator may open.
 *
 * Held in localStorage so the nav and the route guard can answer without a
 * round trip. It is not the access control - the server re-checks every call
 * it gates - but it is what stops a sub-admin being shown doors that will
 * refuse them.
 */
@Injectable({ providedIn: "root" })
export class AdminAccessService {
  constructor(private http: HttpClient) {}

  store(isSuper: boolean, modules: string[]): void {
    try {
      localStorage.setItem(SUPER_KEY, isSuper ? "1" : "0");
      localStorage.setItem(MODULES_KEY, (modules || []).join(","));
    } catch {
      /* Storage unavailable: the guard denies until access is known. */
    }
  }

  clear(): void {
    try {
      localStorage.removeItem(SUPER_KEY);
      localStorage.removeItem(MODULES_KEY);
    } catch {}
  }

  isSuper(): boolean {
    try {
      return localStorage.getItem(SUPER_KEY) === "1";
    } catch {
      return false;
    }
  }

  modules(): string[] {
    try {
      return (localStorage.getItem(MODULES_KEY) || "")
        .split(",")
        .map(v => v.trim())
        .filter(v => !!v);
    } catch {
      return [];
    }
  }

  /**
   * Missing client access state grants no modules. The API remains authoritative.
   */
  can(key: string): boolean {
    if (this.isSuper()) return true;
    return this.modules().indexOf(key) >= 0;
  }

  canAny(keys: string[]): boolean {
    if (this.isSuper()) return true;
    const mine = this.modules();
    return keys.some(k => mine.indexOf(k) >= 0);
  }

  /** Where this administrator should land after signing in. */
  homeRoute(): string {
    for (const m of MODULE_HOME) if (this.can(m.key)) return m.route;
    return "/admin/change-pass";
  }

  /**
   * Re-read from the server, so an access change lands without a sign-out.
   * The callback also receives who the account belongs to - nothing else on
   * the client knows the administrator's name.
   */
  refresh(
    onLoad?: (who: { name: string; email: string; isSuper: boolean }) => void,
    onError?: () => void,
  ): void {
    this.http.get<{ isSuper: boolean; modules: string[]; name: string; email: string }>(
      environment.baseUrl + "admin/team/me",
    ).subscribe(
      res => {
        this.store(!!res.isSuper, res.modules || []);
        if (onLoad) onLoad({ name: res.name || "", email: res.email || "", isSuper: !!res.isSuper });
      },
      () => { if (onError) onError(); },
    );
  }
}
